import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { AppSvgIconComponent, State } from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryListItemResponse } from '@core/api/workspace/models/dataLibraryListItemResponse';
import type { ListDataLibraryApiResponse } from '@core/api/workspace/models/listDataLibraryApiResponse';
import type { ListSystemVariablesApiResponse } from '@core/api/workspace/models/listSystemVariablesApiResponse';
import type { SystemVariableItemResponse } from '@core/api/workspace/models/systemVariableItemResponse';
import type { BoundTextNamespace } from '../../utils/bound-text-template';
import { formatDataLibraryValue } from '../../utils/resolve-linked-variable';

export type SlashMenuItemSource = 'dataLibrary' | 'system';

export interface SlashMenuItem {
  id: string;
  source: SlashMenuItemSource;
  title: string;
  key: string;
  namespace: BoundTextNamespace;
  /** Formatted current / preview value for display. */
  valueLabel: string;
}

export interface SlashMenuAnchorRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

@Component({
  selector: 'app-bound-text-slash-menu',
  standalone: true,
  imports: [AppSvgIconComponent],
  templateUrl: './bound-text-slash-menu.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoundTextSlashMenuComponent
  extends BaseComponent
  implements OnInit
{
  private readonly userAPIService = inject(WorkspaceAPIService);

  readonly open = input(false);
  readonly query = input('');
  readonly anchorRect = input<SlashMenuAnchorRect | null>(null);

  readonly select = output<SlashMenuItem>();
  readonly dismiss = output<void>();

  private readonly listScrollRef =
    viewChild<ElementRef<HTMLElement>>('listScroll');
  private readonly searchInputRef =
    viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly panelRef = viewChild<ElementRef<HTMLElement>>('panel');

  readonly search = signal('');
  readonly selectedIndex = signal(0);
  readonly libraryItems = signal<SlashMenuItem[]>([]);
  readonly systemItems = signal<SlashMenuItem[]>([]);
  readonly menuPosition = signal<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  private readonly listState = new State<ListDataLibraryApiResponse>();
  private readonly systemListState = new State<ListSystemVariablesApiResponse>();
  private searchBoxActive = false;
  /** Last query applied from the parent input — avoid resetting highlight on unrelated CD. */
  private lastSyncedQuery: string | null = null;
  /** After keyboard nav, ignore mouseenter until the pointer moves. */
  private ignoreMouseHighlight = false;

  private static readonly MENU_WIDTH = 300;
  private static readonly MENU_MAX_HEIGHT = 320;
  private static readonly MENU_GAP = 8;
  private static readonly VIEWPORT_PAD = 8;

  readonly filteredLibrary = computed(() =>
    this.filterItems(this.libraryItems(), this.search()),
  );
  readonly filteredSystem = computed(() =>
    this.filterItems(this.systemItems(), this.search()),
  );
  readonly selectableItems = computed(() => [
    ...this.filteredLibrary(),
    ...this.filteredSystem(),
  ]);

  constructor() {
    super();
    effect(() => {
      const isOpen = this.open();
      const rect = this.anchorRect();
      if (!isOpen || !rect) {
        this.menuPosition.set(null);
        this.searchBoxActive = false;
        return;
      }
      this.updateMenuPosition(rect);
    });

    effect(() => {
      const isOpen = this.open();
      const q = this.query();
      if (!isOpen) {
        this.lastSyncedQuery = null;
        return;
      }
      if (this.searchBoxActive) {
        return;
      }
      // Only re-filter / reset highlight when the slash query actually changes.
      if (q === this.lastSyncedQuery) {
        return;
      }
      this.lastSyncedQuery = q;
      this.search.set(q);
      this.selectedIndex.set(0);
      this.loadVariables(q);
      queueMicrotask(() => {
        const searchEl = this.searchInputRef()?.nativeElement;
        if (searchEl && searchEl.value !== q) {
          searchEl.value = q;
        }
      });
    });
  }

  ngOnInit(): void {
    this.loadVariables();
    this.loadSystemVariables();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open()) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    const panel = this.panelRef()?.nativeElement;
    if (panel?.contains(target)) {
      return;
    }
    // Keep menu open while interacting with the bound field.
    if (target.closest('[data-bound-text-slash-anchor]')) {
      return;
    }
    this.dismiss.emit();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent): void {
    if (!this.open()) {
      return;
    }
    this.onKeyDown(event);
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (this.open()) {
      const rect = this.anchorRect();
      if (rect) {
        this.updateMenuPosition(rect);
      }
    }
  }

  onSearchFocus(): void {
    this.searchBoxActive = true;
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchBoxActive = true;
    this.search.set(value);
    this.selectedIndex.set(0);
    this.loadVariables(value);
    queueMicrotask(() => this.scrollSelectedIntoView());
  }

  onOptionMouseDown(event: MouseEvent): void {
    event.preventDefault();
  }

  onPanelMouseMove(): void {
    this.ignoreMouseHighlight = false;
  }

  onOptionMouseEnter(item: SlashMenuItem): void {
    if (this.ignoreMouseHighlight) {
      return;
    }
    const idx = this.selectableItems().findIndex((row) => row.id === item.id);
    if (idx >= 0) {
      this.selectedIndex.set(idx);
    }
  }

  selectItem(item: SlashMenuItem): void {
    this.select.emit(item);
  }

  isSelected(item: SlashMenuItem): boolean {
    const items = this.selectableItems();
    const idx = this.selectedIndex();
    return items[idx]?.id === item.id;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.open()) {
      return;
    }

    const items = this.selectableItems();
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (items.length === 0) {
        return;
      }
      this.ignoreMouseHighlight = true;
      this.selectedIndex.update((i) => (i + 1) % items.length);
      this.scrollSelectedIntoView();
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      if (items.length === 0) {
        return;
      }
      this.ignoreMouseHighlight = true;
      this.selectedIndex.update((i) => (i - 1 + items.length) % items.length);
      this.scrollSelectedIntoView();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      const item = items[this.selectedIndex()];
      if (item) {
        this.select.emit(item);
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.dismiss.emit();
    }
  }

  /** Prefer left of the anchor (over the canvas); else below. */
  private updateMenuPosition(rect: SlashMenuAnchorRect): void {
    const width = BoundTextSlashMenuComponent.MENU_WIDTH;
    const gap = BoundTextSlashMenuComponent.MENU_GAP;
    const pad = BoundTextSlashMenuComponent.VIEWPORT_PAD;
    const maxHeight = BoundTextSlashMenuComponent.MENU_MAX_HEIGHT;
    const spaceLeft = rect.left - pad;

    let left: number;
    let top: number;

    if (spaceLeft >= width + gap) {
      left = rect.left - width - gap;
      top = rect.top;
    } else {
      left = Math.min(
        rect.left,
        Math.max(pad, window.innerWidth - width - pad),
      );
      top = rect.bottom + gap;
    }

    if (top + maxHeight > window.innerHeight - pad) {
      top = Math.max(pad, window.innerHeight - maxHeight - pad);
    }
    if (top < pad) {
      top = pad;
    }
    if (left < pad) {
      left = pad;
    }

    this.menuPosition.set({ top, left, width });
  }

  private scrollSelectedIntoView(): void {
    queueMicrotask(() => {
      const items = this.selectableItems();
      const item = items[this.selectedIndex()];
      const root = this.listScrollRef()?.nativeElement;
      if (!item || !root) {
        return;
      }
      const row = root.querySelector<HTMLElement>(
        `[data-slash-id="${CSS.escape(item.id)}"]`,
      );
      row?.scrollIntoView({ block: 'nearest' });
    });
  }

  private filterItems(items: SlashMenuItem[], query: string): SlashMenuItem[] {
    const q = query.trim().toLowerCase();
    if (!q) {
      return items;
    }
    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.key.toLowerCase().includes(q) ||
        item.valueLabel.toLowerCase().includes(q),
    );
  }

  private loadVariables(search = this.search()): void {
    const trimmed = search.trim();
    this.executeRequest({
      state: this.listState,
      request: this.userAPIService.listDataLibrary({
        ...(trimmed ? { search: trimmed } : {}),
        pageNumber: 1,
        pageSize: 100,
      }),
      handleError: false,
      onSuccess: (response: ListDataLibraryApiResponse) => {
        const mapped = (response.data?.items ?? [])
          .filter(
            (item: DataLibraryListItemResponse) => item.kind === 'variable',
          )
          .map((item) => this.toLibraryItem(item));
        this.libraryItems.set(mapped);
      },
    });
  }

  private loadSystemVariables(): void {
    this.executeRequest({
      state: this.systemListState,
      request: this.userAPIService.listSystemVariables(),
      handleError: false,
      onSuccess: (response: ListSystemVariablesApiResponse) => {
        const mapped = (response.data?.items ?? []).map((item) =>
          this.toSystemItem(item),
        );
        this.systemItems.set(mapped);
      },
    });
  }

  private toLibraryItem(item: DataLibraryListItemResponse): SlashMenuItem {
    return {
      id: `v-${item.id}`,
      source: 'dataLibrary',
      title: item.name,
      key: item.key,
      namespace: 'v',
      valueLabel: formatDataLibraryValue(item.value),
    };
  }

  private toSystemItem(item: SystemVariableItemResponse): SlashMenuItem {
    return {
      id: `s-${item.key}`,
      source: 'system',
      title: item.label,
      key: item.key,
      namespace: 's',
      valueLabel: formatDataLibraryValue(item.previewValue),
    };
  }
}
